import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import PrimaryButton from '../../../components/PrimaryButton';
import client from '../../../lib/feathers';
import NewWineTypeForm from '../../../components/data-management/NewWineTypeForm';
import DeleteConfirmationModal from '../../../components/data-management/DeleteConfirmationModal';

export default function WineTypes() {
  const [wineTypes, setWineTypes] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingWineType, setEditingWineType] = useState(null);
  const [deletingWineType, setDeletingWineType] = useState(null);
  const [loading, setLoading] = useState(true);

  const getWineTypes = async () => {
    try {
      setLoading(true);
      const service = client.service('admin/libation-type');
      const result = await service.find({
        query: {
          $limit: 1000,
          $sort: { name: 1 },
        },
      });
      setWineTypes(result.data);
    } catch (e) {
      console.error('Error fetching wine types:', e);
      toast.error('Failed to load wine types');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getWineTypes();
  }, []);

  const onNewWineType = (newWineType) => {
    setWineTypes([...wineTypes, newWineType].sort((a, b) => a.name.localeCompare(b.name)));
  };

  const onUpdateWineType = (updatedWineType) => {
    setWineTypes(
      wineTypes
        .map((wt) => (wt.id === updatedWineType.id ? updatedWineType : wt))
        .sort((a, b) => a.name.localeCompare(b.name))
    );
    setEditingWineType(null);
  };

  const handleDelete = async () => {
    if (!deletingWineType) return;
    try {
      const service = client.service('admin/libation-type');
      await service.remove(deletingWineType.id);
      setWineTypes(wineTypes.filter((wt) => wt.id !== deletingWineType.id));
      toast.success(`Deleted ${deletingWineType.name}`);
      setDeletingWineType(null);
    } catch (e) {
      console.error('Error deleting wine type:', e);
      toast.error('Failed to delete wine type');
    }
  };

  return (
    <div>
      <div className="sm:flex sm:items-center sm:justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">Wine Types</h3>
        <PrimaryButton onClick={() => setShowCreateModal(true)}>
          New Wine Type
        </PrimaryButton>
      </div>

      <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Name
              </th>
              <th scope="col" className="relative px-6 py-3">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan="2" className="px-6 py-4 text-center text-gray-500">
                  Loading...
                </td>
              </tr>
            ) : wineTypes.length === 0 ? (
              <tr>
                <td colSpan="2" className="px-6 py-4 text-center text-gray-500">
                  No wine types found
                </td>
              </tr>
            ) : (
              wineTypes.map((wineType) => (
                <tr key={wineType.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {wineType.name}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-4">
                    <button
                      onClick={() => setEditingWineType(wineType)}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => setDeletingWineType(wineType)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75" />
            </div>
            <div
              className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full sm:p-6"
              role="dialog"
              aria-modal="true"
            >
              <NewWineTypeForm
                onCancelClick={() => setShowCreateModal(false)}
                onNewWineType={(newWineType) => {
                  onNewWineType(newWineType);
                  setShowCreateModal(false);
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingWineType && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75" />
            </div>
            <div
              className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full sm:p-6"
              role="dialog"
              aria-modal="true"
            >
              <NewWineTypeForm
                wineType={editingWineType}
                onCancelClick={() => setEditingWineType(null)}
                onUpdateWineType={onUpdateWineType}
              />
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingWineType && (
        <DeleteConfirmationModal
          title="Delete Wine Type"
          message={`Are you sure you want to delete "${deletingWineType.name}"? This action cannot be undone.`}
          onConfirm={handleDelete}
          onCancel={() => setDeletingWineType(null)}
        />
      )}
    </div>
  );
}
